import React, { useState } from 'react';
import { auth, supabase } from '../../lib/supabase';
import { redirectToMarketing } from '../../config/domains';

const LoginScreen = ({ onLoginSuccess, onShowRegistration, onShowForgotPassword }) => {
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const { data, error } = await auth.signIn(loginEmail, loginPassword);
      if (error) throw error;
      const user = data?.user;
      if (user) {
        // Fetch user data from the database to get the role - try by ID first, then by email
        let { data: userData, error: userError } = await supabase
          .from('users')
          .select('*')
          .eq('id', user.id)
          .single();

        // If not found by ID, try by email (handles ID mismatch case)
        if (userError && userError.code === 'PGRST116' && user.email) {
          const { data: userByEmail, error: emailError } = await supabase
            .from('users')
            .select('*')
            .eq('email', user.email)
            .single();

          if (!emailError && userByEmail) {
            userData = userByEmail;
            userError = null;
          }
        }

        if (userError) throw userError;

        // Combine auth user with database user data
        const combinedUser = { ...user, ...userData };
        onLoginSuccess(combinedUser);
      } else {
        setLoginError('No user found.');
      }
    } catch (err) {
      // Error caught and ignored
      setLoginError(err.message || 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoginError('');
    setLoginLoading(true);
    try {
      const { error } = await auth.signInWithGoogle();
      if (error) throw error;

      // The actual user handling will be done in the auth state change listener
      // which will handle creating the user record if it's their first time
    } catch (err) {
      // Error caught and ignored
      setLoginError(err.message || 'Google sign-in failed');
      setLoginLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-md">
      <div className="text-center mb-6 sm:mb-8">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            redirectToMarketing();
          }}
          className="inline-block cursor-pointer hover:opacity-80 transition-opacity bg-transparent border-0 p-0"
          title="Go to TeamRezy homepage"
        >
          <img src="/TeamRezyLogo.png" alt="TeamRezy Logo" className="mx-auto mb-4 h-48 w-auto object-contain" />
        </button>
        <p className="text-gray-600 text-sm sm:text-base">Streamline your team dining experience</p>
      </div>
      {/* Login form */}
      <form
        id="login-form"
        onSubmit={handleEmailLogin}
        method="post"
        action="#"
        className="space-y-4 mb-4"
        data-login-form="true"
      >
        <input
          id="login-email"
          type="email"
          name="email"
          value={loginEmail}
          onChange={e => setLoginEmail(e.target.value)}
          placeholder="Email"
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          required
          autoComplete="username"
        />
        <input
          id="login-password"
          type="password"
          name="password"
          value={loginPassword}
          onChange={e => setLoginPassword(e.target.value)}
          placeholder="Password"
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          required
          autoComplete="current-password"
        />
        {loginError && <div className="text-red-600 text-sm">{loginError}</div>}
        <button
          type="submit"
          className="w-full bg-navy-600 text-white py-3 rounded-lg hover:bg-navy-800 transition-colors disabled:bg-gray-400"
          disabled={loginLoading}
          style={{ border: 'none' }}
        >
          {loginLoading ? 'Logging in...' : 'Login'}
        </button>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Or continue with</span>
          </div>
        </div>

        <button
          type="button"
          onClick={handleGoogleLogin}
          disabled={loginLoading}
          className="w-full flex justify-center items-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
          style={{ border: '1px solid #d1d5db' }}
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {loginLoading ? 'Signing in...' : 'Continue with Google'}
        </button>

        <button
          type="button"
          className="w-full mt-4 text-navy-600 hover:text-blue-700 text-sm"
          onClick={onShowForgotPassword}
          style={{ border: 'none' }}
        >
          Forgot Password?
        </button>

        <div className="mt-6 text-center">
          <p className="text-gray-600 text-sm">
            Don't have an account?{' '}
            <button
              onClick={onShowRegistration}
              className="text-navy-600 hover:text-blue-700 font-medium"
              style={{ border: 'none' }}
            >
              Register here
            </button>
          </p>
        </div>

        <div className="mt-4 text-center">
          <p className="text-gray-500 text-xs">
            By using TeamRezy, you agree to our{' '}
            <a
              href="/terms-of-service.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-navy-600 hover:text-blue-700 underline"
            >
              Terms of Service
            </a>
            {' '}and{' '}
            <a
              href="/privacy-policy.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-navy-600 hover:text-blue-700 underline"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </form>
    </div>
  );
};

export default LoginScreen;
